import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'

export const DateRangePresetValues = [
  'today',
  'yesterday',
  'last-calendar-week',
  'last-seven-days',
  'last-fourteen-days',
  'last-thirty-days',
  'last-calendar-month',
  'last-three-calendar-months',
  'last-six-calendar-months',
  'current-year-to-date',
  'last-calendar-year',
  'penultimate-calendar-year',
  // The following values are not relative presets (not based on the current date),
  // so implementation of the getDateRange is up to the implementing components.
  'q1',
  'q2',
  'q3',
  'q4',
  'entire-year',
] as const

export type DateRangePresetValue = (typeof DateRangePresetValues)[number]
export interface DateRangePreset {
  value: DateRangePresetValue
  getDateRange: () => [Date, Date]
}

export type Granularity = '15m' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly'

export type GranularityPresetMap = Record<Granularity, DateRangePreset[]>

const getToday = (): [Date, Date] => {
  const now = new Date()
  return [startOfDay(now), endOfDay(now)]
}

const getYesterday = (): [Date, Date] => {
  const yesterday = subDays(new Date(), 1)
  return [startOfDay(yesterday), endOfDay(yesterday)]
}

const getLastCalendarWeek = (): [Date, Date] => {
  const lastWeek = subWeeks(new Date(), 1)
  return [startOfWeek(lastWeek, { weekStartsOn: 1 }), endOfWeek(lastWeek, { weekStartsOn: 1 })]
}

const getLastSevenDays = (): [Date, Date] => {
  const end = endOfDay(new Date())
  const start = startOfDay(subDays(end, 6))
  return [start, end]
}

const getLastFourteenDays = (): [Date, Date] => {
  const end = endOfDay(new Date())
  const start = startOfDay(subDays(end, 13))
  return [start, end]
}

const getLastThirtyDays = (): [Date, Date] => {
  const end = endOfDay(new Date())
  const start = startOfDay(subDays(end, 29))
  return [start, end]
}

const getLastCalendarMonth = (): [Date, Date] => {
  const lastMonth = subMonths(new Date(), 1)
  return [startOfMonth(lastMonth), endOfMonth(lastMonth)]
}

const getLastThreeCalendarMonths = (): [Date, Date] => {
  const threeMonthsAgo = subMonths(new Date(), 3)
  const lastMonth = subMonths(new Date(), 1)
  return [startOfMonth(threeMonthsAgo), endOfMonth(lastMonth)]
}

const getLastSixCalendarMonths = (): [Date, Date] => {
  const sixMonthsAgo = subMonths(new Date(), 6)
  const lastMonth = subMonths(new Date(), 1)
  return [startOfMonth(sixMonthsAgo), endOfMonth(lastMonth)]
}

const getCurrentYearToDate = (): [Date, Date] => {
  const now = new Date()
  return [startOfYear(now), endOfDay(now)]
}

const getLastCalendarYear = (): [Date, Date] => {
  const lastYear = subYears(new Date(), 1)
  return [startOfYear(lastYear), endOfYear(lastYear)]
}

const getPenultimateCalendarYear = (): [Date, Date] => {
  const penultimateYear = subYears(new Date(), 2)
  return [startOfYear(penultimateYear), endOfYear(penultimateYear)]
}

export const PRESET_TODAY: DateRangePreset = { value: 'today', getDateRange: getToday }
export const PRESET_YESTERDAY: DateRangePreset = { value: 'yesterday', getDateRange: getYesterday }
export const PRESET_LAST_CAL_WEEK: DateRangePreset = { value: 'last-calendar-week', getDateRange: getLastCalendarWeek }

export const PRESET_LAST_7: DateRangePreset = { value: 'last-seven-days', getDateRange: getLastSevenDays }
export const PRESET_LAST_14: DateRangePreset = { value: 'last-fourteen-days', getDateRange: getLastFourteenDays }
export const PRESET_LAST_30: DateRangePreset = { value: 'last-thirty-days', getDateRange: getLastThirtyDays }

export const PRESET_LAST_MONTH: DateRangePreset = { value: 'last-calendar-month', getDateRange: getLastCalendarMonth }
export const PRESET_LAST_3_MONTHS: DateRangePreset = { value: 'last-three-calendar-months', getDateRange: getLastThreeCalendarMonths }
export const PRESET_LAST_6_MONTHS: DateRangePreset = { value: 'last-six-calendar-months', getDateRange: getLastSixCalendarMonths }

export const PRESET_YTD: DateRangePreset = { value: 'current-year-to-date', getDateRange: getCurrentYearToDate }
export const PRESET_LAST_YEAR: DateRangePreset = { value: 'last-calendar-year', getDateRange: getLastCalendarYear }
export const PRESET_PENULTIMATE_YEAR: DateRangePreset = { value: 'penultimate-calendar-year', getDateRange: getPenultimateCalendarYear }

const BASE_PRESETS = [
  PRESET_TODAY,
  PRESET_YESTERDAY,
  PRESET_LAST_CAL_WEEK,
  PRESET_LAST_7,
  PRESET_LAST_14,
  PRESET_LAST_30,
  PRESET_LAST_MONTH,
  PRESET_LAST_3_MONTHS,
  PRESET_LAST_6_MONTHS,
  PRESET_YTD,
]

const MONTH_AND_YEAR = [
  PRESET_LAST_MONTH,
  PRESET_LAST_3_MONTHS,
  PRESET_LAST_6_MONTHS,
  PRESET_YTD,
  PRESET_LAST_YEAR,
  PRESET_PENULTIMATE_YEAR,
]

export const GRANULARITY_PRESETS: GranularityPresetMap = {
  '15m': BASE_PRESETS,
  hourly: BASE_PRESETS,
  daily: BASE_PRESETS,
  weekly: [PRESET_LAST_CAL_WEEK, ...MONTH_AND_YEAR],
  monthly: MONTH_AND_YEAR,
  yearly: [PRESET_LAST_YEAR, PRESET_PENULTIMATE_YEAR],
}
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    value: 'today',
    getDateRange: getToday,
  },
  {
    value: 'yesterday',
    getDateRange: getYesterday,
  },
  {
    value: 'last-calendar-week',
    getDateRange: getLastCalendarWeek,
  },
  {
    value: 'last-seven-days',
    getDateRange: getLastSevenDays,
  },
  {
    value: 'last-fourteen-days',
    getDateRange: getLastFourteenDays,
  },
  {
    value: 'last-thirty-days',
    getDateRange: getLastThirtyDays,
  },
  {
    value: 'last-calendar-month',
    getDateRange: getLastCalendarMonth,
  },
  {
    value: 'last-three-calendar-months',
    getDateRange: getLastThreeCalendarMonths,
  },
  {
    value: 'last-six-calendar-months',
    getDateRange: getLastSixCalendarMonths,
  },
  {
    value: 'current-year-to-date',
    getDateRange: getCurrentYearToDate,
  },
  {
    value: 'last-calendar-year',
    getDateRange: getLastCalendarYear,
  },
  {
    value: 'penultimate-calendar-year',
    getDateRange: getPenultimateCalendarYear,
  },
]

export const getPresetByValue = (value: DateRangePresetValue): DateRangePreset | undefined => {
  return DATE_RANGE_PRESETS.find(preset => preset.value === value)
}

export const filterPresetsWithinRange = (presets: DateRangePreset[], minDate?: Date, maxDate?: Date): DateRangePreset[] => {
  if (!minDate && !maxDate) return presets

  return presets.filter(preset => {
    const [presetStart, presetEnd] = preset.getDateRange()

    if (minDate && presetEnd < minDate) return false
    if (maxDate && presetStart > maxDate) return false

    return true
  })
}
